// Fonction pure d'agrégation des événements calendrier (EPIC E PR1).
// Aucune dépendance React/Supabase. Pas de dépendance à l'horloge :
// le fenêtrage par plage est délégué au service / à FullCalendar.

export type CalendarEventKind = 'hearing' | 'task' | 'prescription' | 'appointment';

export interface CalendarEvent {
  id: string;
  kind: CalendarEventKind;
  title: string;
  start: string;
  allDay: boolean;
  href: string;
  taskId: string | null;
  /** Audience enregistrée. Absent pour l’échéance « prochaine audience » du dossier. */
  hearingId?: string | null;
  color: string;
  dossierId: string | null;
  lawyerId: string | null;
  /** Nom du dossier, absent si l’échéance n’est rattachée à aucun dossier. */
  dossierTitle: string | null;
  /** Texte de la tâche, libellé de l’audience, ou « Prescription ». */
  description: string;
  /** Fin réelle d’un rendez-vous. Les autres événements gardent la durée par défaut. */
  end?: string;
  appointmentId?: string;
  /** Intervenants d’un rendez-vous. Le filtre avocat retient l’événement si l’un d’eux correspond. */
  participantIds?: string[];
  /** Type d’audience ou de rendez-vous, ou tâche type d’une tâche : il donne la couleur. */
  typeId?: string | null;
}

/** Lignes d’une pastille : dossier, puis description. Sans dossier, la description seule. */
export function pillLines(event: Pick<CalendarEvent, 'dossierTitle' | 'description'>): string[] {
  if (event.dossierTitle) return [event.dossierTitle, event.description];
  return [event.description];
}

const PREVIEW_KIND: Record<CalendarEventKind, string> = {
  task: 'Tâche',
  hearing: 'Audience',
  prescription: 'Prescription',
  appointment: 'Rendez-vous',
};

export interface CalendarPreview {
  typeLabel: string;
  dossierTitle: string | null;
  description: string;
  when: string;
  responsable: string | null;
}

/** Champs de la carte au survol. Dossier et responsable absents quand on ne les connaît pas. */
export function calendarPreview(
  event: Pick<CalendarEvent, 'kind' | 'dossierTitle' | 'description' | 'lawyerId'>,
  when: string,
  lawyerName: string | null | undefined,
): CalendarPreview {
  return {
    typeLabel: PREVIEW_KIND[event.kind],
    dossierTitle: event.dossierTitle,
    description: event.description,
    when,
    responsable: event.lawyerId && lawyerName ? lawyerName : null,
  };
}

export type CalendarClick =
  | { action: 'openTask'; taskId: string }
  | { action: 'openAppointment'; appointmentId: string }
  | { action: 'navigate'; href: string };

export function resolveCalendarClick(event: CalendarEvent): CalendarClick | null {
  if (event.kind === 'task' && event.taskId) {
    if (event.dossierId) {
      return {
        action: 'navigate',
        href: `/dossiers/${event.dossierId}/taches?task=${encodeURIComponent(event.taskId)}`,
      };
    }
    return { action: 'openTask', taskId: event.taskId };
  }
  if (event.kind === 'hearing' && event.dossierId) {
    const hearing = event.hearingId
      ? `?hearing=${encodeURIComponent(event.hearingId)}`
      : '';
    return { action: 'navigate', href: `/dossiers/${event.dossierId}/agenda${hearing}` };
  }
  if (event.kind === 'prescription' && event.dossierId) {
    return { action: 'navigate', href: `/dossiers/${event.dossierId}/agenda` };
  }
  if (event.kind === 'appointment' && event.appointmentId) {
    return { action: 'openAppointment', appointmentId: event.appointmentId };
  }
  if (event.href) {
    return { action: 'navigate', href: event.href };
  }
  return null;
}

export interface DossierSrc {
  id: string;
  title: string;
  next_hearing_at: string | null;
  prescription_date?: string | null;
  lead_counsel_id: string | null;
}

export interface TaskSrc {
  id: string;
  content: string;
  deadline: string | null;
  due_date: string | null;
  due_datetime: string | null;
  lawyerId: string | null;
  dossierId: string | null;
  taskTemplateId?: string | null;
}

export interface CalendarFilters {
  lawyerIds: string[];
  dossierId: string | null;
  kinds: CalendarEventKind[];
}

export interface HearingSrc {
  id: string;
  dossierId: string;
  startsAt: string;
  label: string;
  eventTypeId?: string | null;
}

export const EVENT_COLORS: Record<CalendarEventKind, string> = {
  hearing: '#ea7549',
  task: '#043429',
  prescription: '#dc2626',
  appointment: '#c9a84c',
};

export interface AppointmentSrc {
  id: string;
  title: string;
  startsAt: string;
  endsAt: string;
  dossierId: string | null;
  participantIds: string[];
  eventTypeId?: string | null;
}

export function aggregateCalendarEvents(
  dossiers: DossierSrc[],
  tasks: TaskSrc[],
  filters: CalendarFilters,
  hearings: HearingSrc[] = [],
  appointments: AppointmentSrc[] = [],
): CalendarEvent[] {
  const out: CalendarEvent[] = [];
  const covered = new Set(hearings.map((h) => h.dossierId));

  for (const d of dossiers) {
    if (d.next_hearing_at && !covered.has(d.id)) {
      out.push({
        id: `hearing:${d.id}`,
        kind: 'hearing',
        title: `Audience — ${d.title}`,
        start: d.next_hearing_at,
        allDay: false,
        href: `/dossiers/${d.id}/agenda`,
        taskId: null,
        color: EVENT_COLORS.hearing,
        dossierId: d.id,
        lawyerId: d.lead_counsel_id,
        dossierTitle: d.title,
        description: 'Audience',
      });
    }
    if (d.prescription_date) {
      out.push({
        id: `prescription:${d.id}`,
        kind: 'prescription',
        title: `Prescription — ${d.title}`,
        start: d.prescription_date,
        allDay: true,
        href: `/dossiers/${d.id}/agenda`,
        taskId: null,
        color: EVENT_COLORS.prescription,
        dossierId: d.id,
        lawyerId: d.lead_counsel_id,
        dossierTitle: d.title,
        description: 'Prescription',
      });
    }
  }

  for (const h of hearings) {
    const d = dossiers.find((item) => item.id === h.dossierId);
    if (!d) continue;
    out.push({
      id: `hearing:${h.id}`,
      kind: 'hearing',
      title: `${h.label} — ${d.title}`,
      start: h.startsAt,
      allDay: false,
      href: `/dossiers/${d.id}/agenda?hearing=${encodeURIComponent(h.id)}`,
      taskId: null,
      hearingId: h.id,
      typeId: h.eventTypeId ?? null,
      color: EVENT_COLORS.hearing,
      dossierId: d.id,
      lawyerId: d.lead_counsel_id,
      dossierTitle: d.title,
      description: h.label,
    });
  }

  for (const a of appointments) {
    const d = a.dossierId ? dossiers.find((item) => item.id === a.dossierId) : undefined;
    const dossierTitle = d?.title ?? null;
    out.push({
      id: `appointment:${a.id}`,
      kind: 'appointment',
      title: dossierTitle ? `${a.title} — ${dossierTitle}` : a.title,
      start: a.startsAt,
      end: a.endsAt,
      allDay: false,
      href: a.dossierId
        ? `/dossiers/${a.dossierId}/agenda?appointment=${encodeURIComponent(a.id)}`
        : `/calendrier?appointment=${encodeURIComponent(a.id)}`,
      taskId: null,
      appointmentId: a.id,
      typeId: a.eventTypeId ?? null,
      color: EVENT_COLORS.appointment,
      dossierId: a.dossierId,
      lawyerId: a.participantIds[0] ?? null,
      participantIds: a.participantIds,
      dossierTitle,
      description: a.title,
    });
  }

  for (const t of tasks) {
    let start: string | null = null;
    let allDay = true;
    if (t.due_datetime) {
      start = t.due_datetime;
      allDay = false;
    } else if (t.due_date) {
      start = t.due_date;
    } else if (t.deadline) {
      start = t.deadline;
    }
    if (!start) continue;
    out.push({
      id: `task:${t.id}`,
      kind: 'task',
      title: t.content,
      start,
      allDay,
      href: '/taches',
      taskId: t.id,
      typeId: t.taskTemplateId ?? null,
      color: EVENT_COLORS.task,
      dossierId: t.dossierId,
      lawyerId: t.lawyerId,
      dossierTitle: t.dossierId
        ? dossiers.find((d) => d.id === t.dossierId)?.title ?? null
        : null,
      description: t.content,
    });
  }

  const filtered = out.filter((e) => {
    if (filters.lawyerIds.length > 0) {
      const ids = e.participantIds?.length
        ? e.participantIds
        : e.lawyerId
          ? [e.lawyerId]
          : [];
      if (!ids.some((id) => filters.lawyerIds.includes(id))) return false;
    }
    if (filters.dossierId && e.dossierId !== filters.dossierId) return false;
    if (filters.kinds.length > 0 && !filters.kinds.includes(e.kind)) return false;
    return true;
  });

  const seen = new Set<string>();
  const deduped = filtered.filter((e) => {
    if (seen.has(e.id)) return false;
    seen.add(e.id);
    return true;
  });
  deduped.sort((a, b) => a.start.localeCompare(b.start));
  return deduped;
}
